/**
 * DocumentProcessor - Handles document preparation and page setup conversion
 *
 * This module extracts the complex page setup conversion logic from renderText,
 * converting between different page setup formats used throughout the system.
 */

// Debug flag - set to false for production
const DEBUG_ENABLED = false;

import { PageSetupConverter } from '../../../wordprocessing/utils/PageSetupConverter';
import { PAGE_SIZES } from '../../core/types/Typesetter';

export interface DocumentProcessingDeps {
  text: string;
  documentState: { 
    text: string; 
    pageSetup?: any; 
    fontSettings?: any;
    // 🆕 Phase 6 (2026-01-20): Paragraphs with per-paragraph formatting from AST
    paragraphs?: Array<{
      id: string;
      type: string;
      textStart: number;
      textEnd: number;
      version: number;
      // Per-paragraph formatting
      lineSpacing?: number;
      fontSizePx?: number;
      fontFamily?: string;
    }>;
  } | null | undefined;
  fontFamily: string;
  fontWeight: number;
  fontStyle: string;
  fontSizePx: number;
  resolvedFontUid: string;
  debugLog: (...args: any[]) => void;
}

export interface DocumentProcessingResult {
  wasmPageSetup: any;
  convertedPageSetup: any;
  initInput: any;
}

/**
 * Processes document page setup and prepares initialization input
 */
export class DocumentProcessor {
  static processDocument(deps: DocumentProcessingDeps): DocumentProcessingResult {
    const {
      text,
      documentState,
      fontFamily,
      fontWeight,
      fontStyle,
      fontSizePx,
      resolvedFontUid,
      debugLog
    } = deps;

    // Convert page setup from document metadata to WASM format, then to initializeDocument format
    const wasmPageSetup = documentState?.pageSetup
      ? PageSetupConverter.toWasmFormat(documentState.pageSetup)
      : PAGE_SIZES.LETTER;

    debugLog('🎨 [DocumentProcessor] Page setup conversion:', {
      hasMetadata: !!documentState?.pageSetup,
      source: documentState?.pageSetup ? 'Document Metadata' : 'Default (Letter)',
      dbFormat: documentState?.pageSetup,
      wasmFormat: wasmPageSetup
    });

    // Convert WASM PageSetup to initializeDocument format using centralized converter
    const convertedPageSetup = PageSetupConverter.toInitializeDocumentFormat(wasmPageSetup);

    // Debug: Show margin breakdown in pixels for troubleshooting
    const marginTopPx = wasmPageSetup.margins_fp.top / 64;  // Fixed-point to pixels
    const marginRightPx = wasmPageSetup.margins_fp.right / 64;
    const marginBottomPx = wasmPageSetup.margins_fp.bottom / 64;
    const marginLeftPx = wasmPageSetup.margins_fp.left / 64;
    const contentWPx = wasmPageSetup.content_rect_fp.w_fp / 64;
    const contentHPx = wasmPageSetup.content_rect_fp.h_fp / 64;

    DEBUG_ENABLED && console.log('🎨 [DocumentProcessor] 📐 Margin breakdown (pixels):', {
      marginTop: marginTopPx,
      marginRight: marginRightPx,
      marginBottom: marginBottomPx,
      marginLeft: marginLeftPx,
      contentW: contentWPx,
      contentH: contentHPx
    });

    DEBUG_ENABLED && console.log('🎨 [DocumentProcessor] 📐 convertedPageSetup (passed to initializeDocument):', convertedPageSetup);

    // 🆕 Phase 6 (2026-01-20): Use paragraphs from documentState if available
    // This preserves per-paragraph formatting (lineSpacing, fontSizePx, fontFamily) loaded from AST
    const paragraphs = documentState?.paragraphs && documentState.paragraphs.length > 0
      ? documentState.paragraphs.map(p => ({
          id: p.id,
          type: p.type || 'p',
          graphemeStart: p.textStart,
          graphemeEnd: p.textEnd,
          version: p.version || 1,
          // Per-paragraph formatting (only include if defined)
          ...(p.lineSpacing !== undefined && { lineSpacing: p.lineSpacing }),
          ...(p.fontSizePx !== undefined && { fontSizePx: p.fontSizePx }),
          ...(p.fontFamily !== undefined && { fontFamily: p.fontFamily }),
        }))
      : [{ id: 'para-0', type: 'p', graphemeStart: 0, graphemeEnd: text.length, version: 1 }];

    DEBUG_ENABLED && console.log('🎨 [DocumentProcessor] 📄 Using paragraphs:', {
      source: documentState?.paragraphs?.length ? 'documentState' : 'fallback-single',
      count: paragraphs.length,
      withFormatting: paragraphs.filter((p: any) => p.lineSpacing || p.fontSizePx || p.fontFamily).length
    });

    // 🆕 2026-01-20: Log paragraphs with lineSpacing (only when DEBUG_ENABLED)
    if (DEBUG_ENABLED) {
      const parasWithLineSpacing = paragraphs.filter((p: any) => p.lineSpacing !== undefined);
      if (parasWithLineSpacing.length > 0) {
        console.log('📏 [DocumentProcessor] Paragraphs with lineSpacing being sent to WASM:', parasWithLineSpacing.map((p: any) => ({
          id: p.id?.substring(0, 8),
          lineSpacing: p.lineSpacing,
          graphemeStart: p.graphemeStart,
          graphemeEnd: p.graphemeEnd
        })));
      } else {
        console.log('📏 [DocumentProcessor] ⚠️ NO paragraphs have lineSpacing - check AstToTextConverter extraction');
      }
    }

    // Prepare document initialization input
    const initInput = {
      text: text,
      paragraphs,
      pageSetup: convertedPageSetup,
      fonts: [{ fontUid: resolvedFontUid, family: fontFamily, weight: fontWeight, style: fontStyle }],
      defaultFontSizePx: fontSizePx
    };

    return {
      wasmPageSetup,
      convertedPageSetup,
      initInput
    };
  }
}
